import { ThemeComponentSize } from '/#/common/theme'
import type { ComputedRef } from 'vue'

import { defineStore } from 'pinia'
import resolveConfig from 'tailwindcss/resolveConfig'
import { computed, readonly, ref } from 'vue'

import tailwindConfig from '../../../tailwind.config'

/**
 * @name useScreenSizeStore
 * @description Global store for screen size management based on window width
 * Inspired by Vuetify's responsive architecture
 * @see [Figma Breakpoints](https://www.figma.com/design/Hd28nOIFlXADsZkjxc3T8Z/Design-System?node-id=664-906&m=dev)
 * @see [Vuetify Display](https://vuetifyjs.com/en/features/display-and-platform/)
 */
export const useScreenSizeStore = defineStore('screenSize', () => {
  /**
   * @name theme
   * @description Tailwind theme object
   */
  const { theme } = resolveConfig(tailwindConfig)

  /**
   * @name currentWidth
   * @description Current window width. Used to determine the current screen size. It is reactive and will update on window resize.
   */
  const currentWidth = ref(0)

  /**
   * @name currentHeight
   * @description Current window height.
   */
  const currentHeight = ref(0)

  const initialized = ref(false)

  // Event listeners reference
  let resizeListener: (() => void) | null = null

  /**
   * @method removePx
   * @description Remove 'px' from a string and return the number
   * @param value
   * @returns number
   * @example
   * removePx('135px') => 135
   */
  function removePx(value: string): number {
    return Number(value.replace('px', ''))
  }

  /**
   * @name updateDimensions
   * @description Update the current width and height from window
   */
  const updateDimensions = () => {
    if (typeof window !== 'undefined') {
      currentWidth.value = window.innerWidth
      currentHeight.value = window.innerHeight
    }
  }

  /**
   * @name init
   * @description Initialize the store with window dimensions and event listeners
   */
  const init = () => {
    if (initialized.value || typeof window === 'undefined') return

    updateDimensions()

    // Setup resize listener with throttling for better performance
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    resizeListener = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        updateDimensions()
      }, 16) // ~60fps
    }

    window.addEventListener('resize', resizeListener, { passive: true })

    initialized.value = true
  }

  /**
   * @name destroy
   * @description Cleanup event listeners
   */
  const destroy = () => {
    if (resizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', resizeListener)
      resizeListener = null
    }
    initialized.value = false
  }

  /**
   * @name currentScreen
   * @description Current screen size based on the window width
   * @returns string
   */
  const currentScreen: ComputedRef<Exclude<ThemeComponentSize, 'xxs'> | '2xl'> = computed(
    () => {
      const screens = theme.screens as Record<ThemeComponentSize | '2xl', string>
      const width: number = currentWidth.value

      if (width < removePx(screens.xs)) return 'xs'
      if (width >= removePx(screens.xs) && width < removePx(screens.sm)) return 'sm'
      if (width >= removePx(screens.sm) && width < removePx(screens.md)) return 'md'
      if (width >= removePx(screens.md) && width < removePx(screens.lg)) return 'lg'
      if (width >= removePx(screens.lg) && width < removePx(screens.xl)) return 'xl'
      if (width >= removePx(screens.xl) && width < removePx(screens['2xl'])) return '2xl'
      return '2xl'
    },
  )

  // Individual breakpoint checks
  const isXs: ComputedRef<boolean> = computed(() => currentScreen.value === 'xs')
  const isSm: ComputedRef<boolean> = computed(() => currentScreen.value === 'sm')
  const isMd: ComputedRef<boolean> = computed(() => currentScreen.value === 'md')
  const isLg: ComputedRef<boolean> = computed(() => currentScreen.value === 'lg')
  const isXl: ComputedRef<boolean> = computed(() => currentScreen.value === 'xl')
  const is2Xl: ComputedRef<boolean> = computed(() => currentScreen.value === '2xl')

  /**
   * @method smallerThan
   * @description Check if the current screen size is smaller than the given size
   * @param size
   * @returns True if the current screen size is smaller than the given size.
   */
  function smallerThan(size: ThemeComponentSize | '2xl'): boolean {
    const screens = theme.screens as Record<ThemeComponentSize | '2xl', string>
    const width: number = currentWidth.value
    return width < removePx(screens[size])
  }

  /**
   * @method greaterThan (alias for biggerThan)
   * @description Check if the current screen size is greater than the given size
   * @param size
   * @returns True if the current screen size is greater than the given size.
   */
  function greaterThan(size: ThemeComponentSize | '2xl'): boolean {
    const screens = theme.screens as Record<ThemeComponentSize | '2xl', string>
    const width: number = currentWidth.value
    return width > removePx(screens[size])
  }

  /**
   * @method biggerThan
   * @description Check if the current screen size is bigger than or equal to the given size
   * @param size
   * @returns True if the current screen size is bigger than or equal to the given size.
   */
  function biggerThan(size: ThemeComponentSize | '2xl'): boolean {
    const screens = theme.screens as Record<ThemeComponentSize | '2xl', string>
    const width: number = currentWidth.value
    return width >= removePx(screens[size])
  }

  // Device type utility computed properties

  /**
   * @name isMobile
   * @description Check if current screen is mobile size (xs or sm)
   */
  const isMobile = computed(() => isXs.value || isSm.value)

  /**
   * @name isTablet
   * @description Check if current screen is tablet size (md)
   */
  const isTablet = computed(() => isMd.value)

  /**
   * @name isDesktop
   * @description Check if current screen is desktop size (lg, xl, 2xl)
   */
  const isDesktop = computed(() => isLg.value || isXl.value || is2Xl.value)

  // "And up" range utilities
  const smAndUp = computed(() => !isXs.value)
  const mdAndUp = computed(() => isMd.value || isLg.value || isXl.value || is2Xl.value)
  const lgAndUp = computed(() => isLg.value || isXl.value || is2Xl.value)
  const xlAndUp = computed(() => isXl.value || is2Xl.value)

  // "And down" range utilities
  const xsOnly = computed(() => isXs.value)
  const smAndDown = computed(() => isXs.value || isSm.value)
  const mdAndDown = computed(() => isXs.value || isSm.value || isMd.value)
  const lgAndDown = computed(() => !is2Xl.value)

  // Advanced utilities

  /**
   * @name isMobileOrTablet
   * @description Check if current screen is mobile or tablet (xs, sm, md)
   */
  const isMobileOrTablet = computed(() => isMobile.value || isTablet.value)

  /**
   * @name isTouch
   * @description Approximate touch device detection based on screen size
   * Note: This is a heuristic approach, not 100% accurate
   */
  const isTouch = computed(() => isMobile.value)

  /**
   * @name orientation
   * @description Get current screen orientation
   */
  const orientation = computed(() => {
    return currentWidth.value > currentHeight.value ? 'landscape' : 'portrait'
  })

  /**
   * @name isLandscape
   * @description Check if current orientation is landscape
   */
  const isLandscape = computed(() => orientation.value === 'landscape')

  /**
   * @name isPortrait
   * @description Check if current orientation is portrait
   */
  const isPortrait = computed(() => orientation.value === 'portrait')

  /**
   * @name aspectRatio
   * @description Get current screen aspect ratio
   */
  const aspectRatio = computed(() => {
    if (currentHeight.value === 0) return 0
    return currentWidth.value / currentHeight.value
  })

  /**
   * Breakpoint threshold getters
   */
  const thresholds = computed(() => {
    const screens = theme.screens as Record<ThemeComponentSize | '2xl', string>
    return {
      xs: removePx(screens.xs),
      sm: removePx(screens.sm),
      md: removePx(screens.md),
      lg: removePx(screens.lg),
      xl: removePx(screens.xl),
      '2xl': removePx(screens['2xl']),
    }
  })

  return {
    // State
    currentWidth: readonly(currentWidth),
    currentHeight: readonly(currentHeight),
    initialized: readonly(initialized),

    // Actions
    init,
    destroy,
    updateDimensions,

    // Original API for backward compatibility
    screens: theme.screens,
    currentScreen,
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    is2Xl,
    smallerThan,
    biggerThan,

    // Alternative API
    greaterThan,

    // Device type utilities
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
    isTouch,

    // Range utilities (and up)
    smAndUp,
    mdAndUp,
    lgAndUp,
    xlAndUp,

    // Range utilities (and down)
    xsOnly,
    smAndDown,
    mdAndDown,
    lgAndDown,

    // Orientation utilities
    orientation,
    isLandscape,
    isPortrait,
    aspectRatio,

    // Threshold values
    thresholds,
  }
})
